import { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Map, Grid2x2 as Grid, Plus, Minus } from 'lucide-react';
import { supabase, Offer, Category } from '../lib/supabase';
import { OfferCard } from '../components/OfferCard';
import { DateRangePicker } from '../components/DateRangePicker';
import { SEO } from '../components/SEO';
import { StructuredData } from '../components/StructuredData';

const OffersMap = lazy(() => import('../components/OffersMap').then(m => ({ default: m.OffersMap })));

export interface OfferRatings {
  [offerId: string]: { average: number; count: number };
}

function OfferCardSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse">
      <div className="h-64 bg-gray-200" />
      <div className="p-4 space-y-3">
        <div className="h-5 bg-gray-200 rounded w-3/4" />
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="flex justify-between items-center">
          <div className="h-6 bg-gray-200 rounded w-24" />
          <div className="h-9 bg-gray-200 rounded w-24" />
        </div>
      </div>
    </div>
  );
}

export function Home() {
  const navigate = useNavigate();
  const resultsRef = useRef<HTMLElement>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allOffers, setAllOffers] = useState<Offer[]>([]);
  const [filteredOffers, setFilteredOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [offerRatings, setOfferRatings] = useState<OfferRatings>({});
  const [currencyRates, setCurrencyRates] = useState<{ [key: string]: number }>({});
  const [searchParams, setSearchParams] = useState({
    category: '',
    checkIn: '',
    checkOut: '',
    guests: 1,
  });

  useEffect(() => {
    const abortController = new AbortController();

    const loadCriticalData = async () => {
      const [categoriesRes, offersRes] = await Promise.all([
        supabase.from('categories').select('*').order('created_at'),
        supabase.from('offers').select('*').eq('is_available', true).order('created_at', { ascending: false }),
      ]);

      if (abortController.signal.aborted) return;

      if (categoriesRes.data) setCategories(categoriesRes.data);
      if (offersRes.data) {
        setAllOffers(offersRes.data);
        setFilteredOffers(offersRes.data);
      }
      setLoading(false);

      const [reviewsRes, ratesRes] = await Promise.all([
        supabase.from('reviews').select('offer_id, rating'),
        supabase.from('currency_rates').select('currency_code, rate'),
      ]);

      if (abortController.signal.aborted) return;

      if (reviewsRes.data) {
        const ratings: OfferRatings = {};
        const grouped: { [id: string]: number[] } = {};
        reviewsRes.data.forEach((r: { offer_id: string; rating: number }) => {
          if (!grouped[r.offer_id]) grouped[r.offer_id] = [];
          grouped[r.offer_id].push(r.rating);
        });
        Object.entries(grouped).forEach(([offerId, ratingsList]) => {
          const avg = ratingsList.reduce((s, v) => s + v, 0) / ratingsList.length;
          ratings[offerId] = { average: Math.round(avg * 10) / 10, count: ratingsList.length };
        });
        setOfferRatings(ratings);
      }

      if (ratesRes.data) {
        const rates: { [key: string]: number } = {};
        ratesRes.data.forEach((r: { currency_code: string; rate: number }) => {
          rates[r.currency_code] = r.rate;
        });
        setCurrencyRates(rates);
      }
    };

    loadCriticalData();
    return () => abortController.abort();
  }, []);

  const handleSearch = async () => {
    setIsSearching(true);
    let filtered = [...allOffers];

    if (searchParams.category) {
      filtered = filtered.filter(offer => offer.category_id === searchParams.category);
    }

    if (searchParams.guests > 1) {
      filtered = filtered.filter(offer => offer.capacity && offer.capacity >= searchParams.guests);
    }

    if (searchParams.checkIn && searchParams.checkOut) {
      const availableOfferIds = await checkAvailability(
        filtered.map(o => o.id),
        searchParams.checkIn,
        searchParams.checkOut
      );
      filtered = filtered.filter(offer => availableOfferIds.includes(offer.id));
    }

    setHasSearched(true);
    setFilteredOffers(filtered);
    setIsSearching(false);

    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const checkAvailability = async (
    offerIds: string[],
    checkIn: string,
    checkOut: string
  ): Promise<string[]> => {
    const blockedOfferIds = new Set<string>();

    const { data: blockedDates } = await supabase
      .from('blocked_dates')
      .select('offer_id, blocked_date')
      .in('offer_id', offerIds)
      .gte('blocked_date', checkIn)
      .lt('blocked_date', checkOut);

    (blockedDates || []).forEach(d => blockedOfferIds.add(d.offer_id));

    const { data: manualBlocks } = await supabase
      .from('manual_blocks')
      .select('offer_id, block_date')
      .in('offer_id', offerIds)
      .gte('block_date', checkIn)
      .lt('block_date', checkOut);

    (manualBlocks || []).forEach(d => blockedOfferIds.add(d.offer_id));

    const { data: reservations } = await supabase
      .from('reservations')
      .select('offer_id, start_date, end_date')
      .in('offer_id', offerIds)
      .eq('status', 'confirmed')
      .or(`and(start_date.lt.${checkOut},end_date.gt.${checkIn})`);

    (reservations || []).forEach(r => blockedOfferIds.add(r.offer_id));

    const { data: importedEvents } = await supabase
      .from('ical_imported_events')
      .select('offer_id, start_date, end_date')
      .in('offer_id', offerIds)
      .or(`and(start_date.lt.${checkOut},end_date.gt.${checkIn})`);

    (importedEvents || []).forEach(e => blockedOfferIds.add(e.offer_id));

    return offerIds.filter(id => !blockedOfferIds.has(id));
  };

  return (
    <div>
      <SEO
        title="LocationOran.com - Location de Vacances à Oran"
        description="Trouvez et réservez les meilleures locations de vacances à Oran : appartements, villas, voitures, scooters et activités. Réservation en ligne simple et sécurisée."
        keywords="location vacances oran, appartement oran, villa oran, location voiture oran"
      />
      <StructuredData type="organization" />
      <section className="relative h-[600px] md:h-[500px] text-white pt-20 md:pt-0">
        <picture>
          <source srcSet="/hero-oran.webp" type="image/webp" />
          <img
            src="/hero-oran.jpg"
            alt="Vue panoramique d'Oran, Algérie"
            fetchPriority="high"
            decoding="sync"
            width={1920}
            height={600}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </picture>
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative container mx-auto px-4 h-full flex flex-col items-center justify-center">
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-4 text-center px-4 mt-8 md:mt-0">Location Oran - Appartement, Villa, Hotel & Voiture</h1>
          <p className="text-sm md:text-lg lg:text-xl mb-8 max-w-2xl text-center px-4">
            Réservez votre hébergement et location à Oran : appartements, villas, hotels, voitures et activités. Meilleur site de location nuitée à Oran, Algérie.
          </p>

          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="category-select" className="block text-sm font-medium text-gray-700 mb-2">
                  Type de location
                </label>
                <select
                  id="category-select"
                  value={searchParams.category}
                  onChange={(e) => setSearchParams({ ...searchParams, category: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                >
                  <option value="">Tous les types</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <DateRangePicker
                startDate={searchParams.checkIn}
                endDate={searchParams.checkOut}
                onDateChange={(checkIn, checkOut) => setSearchParams({
                  ...searchParams,
                  checkIn,
                  checkOut
                })}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Voyageurs
                </label>
                <div className="flex items-center bg-white border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setSearchParams({
                      ...searchParams,
                      guests: Math.max(1, searchParams.guests - 1)
                    })}
                    className="p-3 hover:bg-gray-100 transition text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={searchParams.guests <= 1}
                    aria-label="Réduire le nombre de voyageurs"
                  >
                    <Minus size={20} />
                  </button>
                  <div className="flex-1 flex items-center justify-center space-x-2 py-3 text-gray-800">
                    <Users size={20} className="text-gray-400" />
                    <span className="font-medium text-lg">{searchParams.guests}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSearchParams({
                      ...searchParams,
                      guests: Math.min(20, searchParams.guests + 1)
                    })}
                    className="p-3 hover:bg-gray-100 transition text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={searchParams.guests >= 20}
                    aria-label="Augmenter le nombre de voyageurs"
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="w-full mt-4 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition transform hover:scale-[1.02] flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isSearching ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Recherche en cours...</span>
                </>
              ) : (
                <>
                  <Search size={20} />
                  <span>Rechercher</span>
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      {hasSearched && filteredOffers.length === 0 && (
        <section ref={resultsRef} className="bg-gray-50 py-16 animate-fadeIn">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-2xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="text-6xl mb-4">😔</div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  Aucun résultat trouvé
                </h2>
                <p className="text-gray-600 mb-6">
                  Nous n'avons trouvé aucune location correspondant à vos critères de recherche.
                  Essayez de modifier vos filtres ou contactez-nous pour plus d'informations.
                </p>
                <button
                  onClick={() => {
                    setSearchParams({
                      category: '',
                      checkIn: '',
                      checkOut: '',
                      guests: 1,
                    });
                    setHasSearched(false);
                    setFilteredOffers(allOffers);
                  }}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  Réinitialiser la recherche
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {(loading || filteredOffers.length > 0) && (
        <section ref={resultsRef} className="bg-gray-50 py-16 animate-fadeIn">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 md:mb-12 gap-4">
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800">
                {hasSearched ? 'Résultats de recherche' : 'Locations Recommandées'}
              </h2>

              <div className="flex bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  aria-label="Afficher en grille"
                  className={`flex items-center px-3 md:px-4 py-2 transition ${
                    viewMode === 'grid'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Grid size={18} className="md:mr-2" />
                  <span className="hidden md:inline">Grille</span>
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  aria-label="Afficher sur la carte"
                  className={`flex items-center px-3 md:px-4 py-2 transition ${
                    viewMode === 'map'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Map size={18} className="md:mr-2" />
                  <span className="hidden md:inline">Carte</span>
                </button>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {Array.from({ length: 6 }).map((_, i) => (
                  <OfferCardSkeleton key={i} />
                ))}
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredOffers.map((offer, index) => (
                  <OfferCard
                    key={offer.id}
                    offer={offer}
                    rating={offerRatings[offer.id]}
                    currencyRates={currencyRates}
                    priority={index < 3}
                    searchParams={{
                      checkIn: searchParams.checkIn,
                      checkOut: searchParams.checkOut,
                      guests: searchParams.guests
                    }}
                  />
                ))}
              </div>
            ) : (
              <Suspense fallback={<div className="w-full h-[400px] md:h-[500px] lg:h-[600px] rounded-xl bg-gray-100 animate-pulse flex items-center justify-center"><span className="text-gray-500">Chargement de la carte...</span></div>}>
                <OffersMap
                  offers={filteredOffers}
                  onOfferClick={(offer) => navigate(`/offer/${offer.id}`, {
                    state: {
                      checkIn: searchParams.checkIn,
                      checkOut: searchParams.checkOut,
                      guests: searchParams.guests
                    }
                  })}
                />
              </Suspense>
            )}
          </div>
        </section>
      )}

      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">
            Location à Oran - Votre Guide Complet
          </h2>
          <div className="max-w-4xl mx-auto text-gray-700 space-y-6">
            <p className="text-lg">
              Vous cherchez une <strong>location à Oran</strong> ? Notre plateforme vous offre le meilleur choix d'hébergements et services à Oran, Algérie. Que vous cherchiez un <strong>appartement Oran</strong>, une <strong>villa Oran</strong>, un <strong>hotel Oran</strong> ou une <strong>location voiture Oran</strong>, nous avons ce qu'il vous faut.
            </p>

            <h3 className="text-2xl font-bold text-gray-800 mt-8">Hotels et Hébergement à Oran</h3>
            <p>
              Trouvez les <strong>meilleurs hotels à Oran</strong> avec notre sélection complète d'hébergements. Des <strong>luxury hotels in Oran</strong> aux <strong>cheap hotels in Oran</strong>, nous proposons tous les types d'<strong>Oran accommodation</strong>. Réservez votre <strong>hotel Oran</strong> facilement en ligne et profitez des <strong>best hotels in Oran</strong> près de la plage ou en centre-ville.
            </p>

            <h3 className="text-2xl font-bold text-gray-800 mt-8">Location Nuitée Oran - Appartements et Villas</h3>
            <p>
              Notre service de <strong>location nuitée Oran</strong> vous permet de <strong>louer à Oran</strong> des appartements et villas meublés pour vos courts ou longs séjours. Chaque <strong>appartement Oran</strong> est équipé pour votre confort avec toutes les commodités modernes.
            </p>

            <h3 className="text-2xl font-bold text-gray-800 mt-8">Location Voitures Oran</h3>
            <p>
              Besoin d'une <strong>location voiture Oran</strong> ? Découvrez nos offres de <strong>location voitures Oran</strong> aux meilleurs prix. Déplacez-vous librement dans Oran et ses environs avec nos véhicules de qualité.
            </p>

            <h3 className="text-2xl font-bold text-gray-800 mt-8">Activités à Oran</h3>
            <p>
              Explorez nos offres d'<strong>activités Oran</strong> : visites guidées, excursions, sports nautiques et bien plus encore. Profitez de votre séjour à Oran avec des expériences inoubliables.
            </p>

            <h3 className="text-2xl font-bold text-gray-800 mt-8">Pourquoi Réserver avec Nous ?</h3>
            <ul className="list-disc list-inside space-y-2">
              <li>Réservation en ligne simple et sécurisée</li>
              <li>Large choix d'<strong>Oran lodging</strong> et hébergements</li>
              <li>Prix compétitifs pour tous les budgets</li>
              <li>Service client disponible 24/7</li>
              <li>Annulation flexible</li>
              <li>Avis clients vérifiés</li>
            </ul>

            <p className="text-lg mt-8">
              Que vous planifiez un séjour d'affaires ou des vacances à Oran, notre plateforme est votre meilleur allié pour trouver l'hébergement parfait. Réservez dès maintenant votre <strong>hotel Oran</strong>, <strong>appartement</strong>, <strong>villa</strong> ou <strong>location voiture</strong> et découvrez la perle de l'Ouest algérien !
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
